<template lang="pug" id="carousel">
.message-carousel-wrapper(:class="{'message-webchat': inWebchat, 'is-responsive': false  }")
  .selected(v-if="replied")
    .message
      | {{ this.repliedAgentMessage }}
  .selected(v-else-if="expired")
    .message
      | {{ this.expiredMessage }}
  .carousel(v-else)
    .carousel-search-container(v-if="!isDisabled")
      label.hidden(for="carousel-agent-search") name/location/specialty
      aiq-input(clearable
                :suffix-icon="Search"
                size="small"
                ref="searchRef"
                :placeholder="searchPlaceHolder"
                @clear="clear"
                @keyup.enter="search"
                v-model="query"
                id="carousel-agent-search")
    .carousel-content(:class="setSingleCard")
      template(v-if="!showBlankAgent")
        component.carousel-content-item(v-for="(item, index) in attachments"
                                          :is="getCarouselItemComponent(item)"
                                          :key="`{item.agent.id}-${index}`"
                                          :item="Object.assign({}, item)"
                                          :visible="isVisible(index)"
                                          :disabled="isDisabled"
                                          @selectItem="selectItem"
                                          :class="{ 'is-selected': selectionMap[index] }"
                                          :selected="selectionMap[index]"
                                          :accountFieldsVisibility="accountFieldsVisibility"
                                          :labels="carouselLabels")
      template(v-if="showBlankAgent")
        component.carousel-content-item(:is="getCarouselItemComponent(blankAgent)"
                                          :item="blankAgent"
                                          :visible="true"
                                          :accountFieldsVisibility="accountFieldsVisibility"
                                          :labels="carouselLabels"
                                          :disabled="true")
      .carousel-content-item.carousel-loading-indicator-container
        .carousel-loading-indicator(v-if="isPaging || isSearching")
          .filter-loading(slot="spinner")
            span.filter-loading-item(v-for="item in 3")

</template>

<script>
/* eslint-disable no-underscore-dangle */
import { nextTick } from 'vue';
import upperFirst from 'lodash/upperFirst';
import throttle from 'lodash/throttle';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import RepliesMessage from './RepliesMessage.vue';
import AgentCarouselItem from './carouselItems/AgentCarouselItem.vue';
import { ifScrolledToEndOfContainer } from '../../../lib/scroller';
import { Search } from '@element-plus/icons-vue';

const SCROLL_EDGE_THRESHOLD = 20;

export default {
  extends: RepliesMessage,
  name: 'CarouselMessage',
  components: {
    AgentCarouselItem,
  },
  data() {
    return {
      visibleMap: {},
      query: '',
      Search,
      isSingleCard: false,

    };
  },
  computed: {
    accountFieldsVisibilityConfig() {
      return this.accountFieldsVisibility
        .reduce((acc, item) => ({ ...acc, [item.name]: item }), {});
    },
    // TODO: pull the text for "Select New Banker" from the configuration.
    selected() {
      return this.attachments[Object.keys(this.selectionMap)[0]];
    },
    searchPlaceHolder() {
      return this.getString('messages.carousel.search_placeholder', 'name/location/specialty');
    },
    carouselLabels() {
      return {
        expertise: this.getString('messages.carousel.expertise', 'Expertise'),
        about_me: this.getString('messages.carousel.about_me', 'About me'),
        hours_available: this.getString('messages.carousel.hours_available', 'Hours Available'),
        location: this.getString('messages.carousel.location', 'Location'),
      };
    },
    replied() {
      return Boolean(get(this.message, 'replyitem'));
    },
    expired() {
      return Boolean(get(this.message, 'expired'));
    },
    blankAgent() {
      const tagline = this.getString('messages.carousel.tagline', 'Sorry, no matching agents.');
      const buttonContent = this.getString('messages.carousel.no_match', 'No Matches');

      return {
        message_type: 'agent',
        agent: {
          tagline,
          background_image_url: '',
          profile_image_url: '',
          selection: {
            reply: { content: buttonContent },
            message_type: 'reply',
          },
        },
      };
    },
    repliedAgentMessage() {
      let agentName = '';
      let agentLabel = '';
      let agentMsg = '';
      const canShowDisplayName = get(this.accountFieldsVisibilityConfig, 'display_name.show', true);
      if (this.inWebchat) {
        const { first_name, display_name } = get(this.message, 'replyitem.reply', {});
        agentName = canShowDisplayName && !isEmpty(display_name) ? display_name : first_name;
        agentName = agentName || get(this.message, 'replyitem.reply.content', '').replace('Select ', '');
        if (agentName) {
          agentMsg = this.getString('messages.carousel.selected_named_agent', {
            agentName,
            defaultValue: 'You selected {{agentName}} as your banker',
          });
        } else {
          agentMsg = this.getString('messages.carousel.selected_unnamed_agent', 'You selected a banker');
        }
      } else {
        const { first_name, display_name } = get(this.message, 'replyitem', {});
        agentName = canShowDisplayName && !isEmpty(display_name) ? display_name : first_name;
        agentName = agentName || get(this.message, 'replyitem.content', '').replace('Select ', '');
        agentLabel = agentName ? `${agentName} as their banker` : 'a banker';
        agentMsg = `The customer selected ${agentLabel}`;
      }

      return agentMsg;
    },
    expiredMessage() {
      return this.getString('messages.carousel.expired', 'Selection not made');
    },
    setSingleCard() {
      return this.totalAgents === 0 || this.isSingleCard
        ? 'carousel-content-single'
        : '';
    },
    isPaging() {
      return get(this.message, 'payload.carousel.isPaging', false);
    },
    isSearching() {
      return get(this.message, 'payload.carousel.isSearching', false);
    },
    totalAgents() {
      const agentCount = get(this.message, 'payload.carousel.attachments.length', 0);
      return agentCount;
    },
    showBlankAgent() {
      return !(this.isPaging || this.isSearching) && this.totalAgents === 0;
    },
  },
  watch: {
    isPaging(newVal) {
      this.handleLoadingChange(newVal);
    },
  },
  beforeUnmount() {
    const carousel = this.getCarouselDomElement();
    if (carousel) {
      carousel.removeEventListener('scroll', this.throttledScrollHandler);
    }
  },
  mounted() {
    const _throttled = throttle(this.handleScroll, 1000);
    this.throttledScrollHandler = (e) => _throttled(e.target);
    const carousel = this.getCarouselDomElement();
    if (carousel) {
      carousel.addEventListener('scroll', this.throttledScrollHandler);
    }
  },
  methods: {
    isAgents() {
      return this.totalAgents !== 0;
    },
    getCarouselDomElement() {
      if (this.$el && this.$el.getElementsByClassName) {
        const carousels = this.$el.getElementsByClassName('carousel-content');
        return (carousels && carousels.length) ? carousels[0] : undefined;
      }

      return undefined;
    },
    handleLoadingChange(isOn) {
      if (isOn) {
        const carouselContainer = this.$el;
        nextTick()
          .then(() => {
            if (carouselContainer) {
              const infiniteLoads = carouselContainer.getElementsByClassName('carousel-loading-indicator');
              if (infiniteLoads && infiniteLoads.length === 1) {
                infiniteLoads[0].scrollIntoView({ behavior: 'smooth' });
              }
            }
          });
      }
    },
    getNextPage(forceLoadFirstPage = false) {
      if (!this.message.payload.carousel.pagination) {
        return;
      }
      const { limit, rowCount } = this.message.payload.carousel.pagination;
      const offset = this.attachments.length;
      // Don't paginate if we are at the last page.
      if (forceLoadFirstPage || (rowCount && offset < rowCount)) {
        const payload = {
          message: this.message,
          query: this.query,
          paginate: {
            limit, offset,
          },
        };
        this.$emit('paginate', payload);
        this.blurSearchFieldToHideKeyboard();
      }
    },
    handleScroll(scrollEvent) {
      // We will get a scrolling event that looks like
      // we scrolled to the end of the container when the agent list is
      // cleared out, so ignore isSearching state.
      if (!this.isSearching
          && ifScrolledToEndOfContainer(scrollEvent, { threshold: SCROLL_EDGE_THRESHOLD })) {
        this.getNextPage();
      }
    },
    isVisible(index) {
      return this.visibleMap[index];
    },
    clearAttachments() {
      this.message.payload[this.type].attachments = [];
    },
    clear() {
      this.clearAttachments();
      nextTick().then(() => {
        this.getNextPage(true);
      });
    },
    search() {
      this.clearAttachments();
      const query = this.query
        .trim()
        .toLowerCase();

      if (query) {
        this.$emit('search', { query, message: this.message });
      }
      this.blurSearchFieldToHideKeyboard();
    },
    blurSearchFieldToHideKeyboard() {
      if (this.$refs && this.$refs.searchRef) {
        this.$refs.searchRef.blur();
      }
    },
    getCarouselItemComponent(item) {
      return `${upperFirst(item.message_type)}CarouselItem`;
    },
    getCarouselRect() {
      return this.$el.querySelector('.carousel').getBoundingClientRect();
    },
    getCards() {
      return this.$el.querySelectorAll('.carousel-content-item');
    },
    getCardInViewIndex() {
      const carouselX = this.getCarouselRect().x;
      return Array.from(this.getCards()).findIndex(card => {
        const { x } = card.getBoundingClientRect();
        return x >= carouselX;
      });
    },
    navigatePrevious() {
      const cardInViewIndex = this.getCardInViewIndex();
      if (cardInViewIndex < 1) {
        return;
      }
      this.getCards()[cardInViewIndex - 1].scrollIntoView({
        behavior: 'smooth',
      });
    },
    navigateNext() {
      const cards = this.getCards();
      const cardInViewIndex = this.getCardInViewIndex();
      if (cardInViewIndex + 1 >= cards.length) {
        return;
      }
      this.getCards()[cardInViewIndex + 1].scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    },
    itemFinder(item) {
      return d => {
        const key = d.message_type;
        return d[key].id === item.id;
      };
    },
    prepareReply(item) {
      const key = item.message_type;
      const reply = item[key];
      return reply.selection;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../aiq-theme/aiq-animations";
@import "../../../aiq-theme/aiq-variables";

$carousel-gutter-width: 8px;

@mixin navigation-item {
  border-radius: 16px;
  height: 100px;
  width: 30px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  color: #fff;
  background: rgba(119, 155, 183, 0.2);
  font-size: 20px;
  cursor: pointer;
}

.selected {
  color: #99A9BF;
  margin: 0 auto;
  padding-bottom: 1em;
  padding-top: 1em;
  text-align: center;

  .message {
    font-size: 1.05em;
  }
}

.carousel {
  position: relative;
  overflow-x: auto;
  width: 100%;
}

.carousel-content {
  display: flex;
  overflow-y: scroll;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch; /* Needed for scroll-snap to work on iOS */
}

.carousel-content-single {
  justify-content: center;
}

.carousel-content-item {
  scroll-snap-align: center;
  margin: $carousel-gutter-width calc($carousel-gutter-width / 2);

  &.carousel-loading-indicator-container {
    display: flex;
  }

  > .carousel-loading-indicator {
    width: 45px;
    height: auto;
    margin-right: 15px;
    align-self: center;
  }
}

.carousel-search-container {
  background-color: $aiq-mobile-agent;
  padding: 0.5rem;

  .hidden {
    font-size: 0px;
  }
}

.navigate__previous {
  @include navigation-item;
  left: 8px;
  top: 50%;
}

.navigate__next {
  @include navigation-item;
  right: 8px;
  top: 50%;
}
</style>
